import React, { useCallback, useState } from 'react';

import Modal from 'components/modal';
import useEffectOnce from 'hooks/use-effect-once';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LANGUAGES } from 'state/intl/types';
import { theme } from 'styles/configure-theme';
import { Region } from 'utils/environment/types';
import { setLanguageAndRestart } from 'utils/intl/language';

import LanguageSelectorModalContents from './language-selector-modal-contents';
import { ModalAdditionalContentWrapper } from './language-selector-modal-contents/styled';
import { ContentContainer, PrimaryText, SecondaryText } from './language-selector-modal.styled';
import { ILanguageModalProps } from './types';

const LanguageSelectorModal: React.FC<React.PropsWithChildren<ILanguageModalProps>> = ({
  onModalDismiss,
  heading,
  primaryText,
  secondaryText,
  disclaimer,
}) => {
  const { region, language } = useLocale();
  const [{ langKey, regionKey }, setSelectedLocale] = useState({
    langKey: language,
    regionKey: region,
  });

  const handleSelection = (selectedLanguage: string, mappedRegion: string) => {
    setSelectedLocale({
      langKey: selectedLanguage as LANGUAGES,
      regionKey: mappedRegion as Region,
    });
  };

  const noChange = langKey === language;
  const setLanguageHandler = useCallback(() => {
    if (noChange) {
      onModalDismiss();
      return;
    }

    setLanguageAndRestart(langKey);
  }, [noChange, langKey, onModalDismiss]);

  useEffectOnce(() => {
    logEvent(CustomEventNames.LOCALE_SELECTOR_MODAL_SHOWN, EventTypes.Navigation);
  });

  const applyButtonText = t('apply');
  const modalHeading = heading || t('selectLanguage');

  return (
    <Modal
      testID="select-language-modal"
      onDismiss={onModalDismiss}
      amplitudeEventData={{
        modalAppearanceEventMessage: 'Language and region selector',
      }}
      header={modalHeading}
    >
      <ContentContainer paddingTop="$13.5" background={theme.token('background-dialog')}>
        {(primaryText || secondaryText) && (
          <ModalAdditionalContentWrapper>
            {primaryText && <PrimaryText variant="copyOne">{primaryText}</PrimaryText>}
            {secondaryText && <SecondaryText variant="copyOne">{secondaryText}</SecondaryText>}
          </ModalAdditionalContentWrapper>
        )}
        <LanguageSelectorModalContents
          ariaRadioGroupLabeledBy="language-select-modal-heading"
          regionKey={regionKey}
          langKey={langKey}
          disclaimer={disclaimer}
          applyButtonText={applyButtonText}
          setLanguage={setLanguageHandler}
          handleSelection={handleSelection}
        />
      </ContentContainer>
    </Modal>
  );
};

export default LanguageSelectorModal;
